import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiZap, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Badge } from '@/shared/components/ui/Badge';
import { toast } from 'sonner';
import { DynamicConfigForm } from './DynamicConfigForm';
import { AppIntegration, ProviderDefinition, ConfigField, CATEGORY_LABELS, STATUS_LABELS, STATUS_VARIANTS, IntegrationStatus } from '../types';

interface IntegrationFormDrawerProps {
  integrationId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

// Provider oficial dedicado (WhatsApp 2FA + Notificacoes do sistema) — hospeda
// 3 templates (`troia_2fa` + `troia_notification_v2_<app>` +
// `troia_notification_l8_<app>` — os de notificacao tem sufixo por-app desde
// 2026-08-03, pois apps distintos compartilham a mesma WABA e a URL base do
// botao e fixada na criacao; l8 e enhancement progressivo pra mensagens
// maiores — layout "OPCAO 2" de paragrafos). O status de cada um
// vive em `integration.config.templates.{twoFactor,notification,notificationLarge}.status`.
const WHATSAPP_NOTIFICATIONS_PROVIDER_ID = 'whatsapp-business-notifications';

interface SystemTemplateStatusEntry {
  status?: string;
}

interface SystemTemplatesConfig {
  templates?: {
    twoFactor?: SystemTemplateStatusEntry;
    notification?: SystemTemplateStatusEntry;
    notificationLarge?: SystemTemplateStatusEntry;
  };
}

interface ProvisionTemplatesResult {
  twoFactor: SystemTemplateStatusEntry | null;
  notification: SystemTemplateStatusEntry | null;
  notificationLarge: SystemTemplateStatusEntry | null;
}

const TEMPLATE_STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Aprovado',
  PENDING: 'Pendente',
  REJECTED: 'Rejeitado',
};

const TEMPLATE_STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'error'> = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'error',
};

const templateStatusLabel = (status?: string): string =>
  status ? (TEMPLATE_STATUS_LABELS[status] ?? status) : 'Ausente';

const templateStatusVariant = (status?: string): 'success' | 'warning' | 'error' | 'outline' =>
  (status && TEMPLATE_STATUS_VARIANTS[status]) || 'outline';

// `config` chega tipado como Record<string, unknown> (bag generico do provider) —
// leitura estreitada aqui em vez de alargar o tipo AppIntegration.config global.
const getTemplateStatus = (
  config: Record<string, unknown> | undefined,
  kind: 'twoFactor' | 'notification' | 'notificationLarge'
): string | undefined => (config as SystemTemplatesConfig | undefined)?.templates?.[kind]?.status;

export const IntegrationFormDrawer: React.FC<IntegrationFormDrawerProps> = ({
  integrationId,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!integrationId;

  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [status, setStatus] = useState<IntegrationStatus>('active');

  // Fetch providers (conditioned on isOpen)
  const { data: providers } = useQuery({
    queryKey: ['admin-providers'],
    queryFn: () => apiGet<ProviderDefinition[]>('/integrations/providers'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch integration details when editing (conditioned on isOpen)
  const { data: integration } = useQuery({
    queryKey: ['admin-integration', integrationId],
    queryFn: () => apiGet<AppIntegration>(`/integrations/${integrationId}`),
    enabled: isOpen && isEditing,
  });

  // Tracks o ID da integration cujos dados já foram populados no form.
  // Evita que refetches do React Query (refetchOnWindowFocus, refetchOnMount)
  // sobrescrevam o estado do form depois que o user já editou — sem isso,
  // mudar pra outra aba e voltar ZERA as alterações em andamento.
  const populatedForIdRef = useRef<string | null>(null);

  // Populate form when editing — só na primeira vez que carrega cada integration
  useEffect(() => {
    if (integration && isEditing && populatedForIdRef.current !== integration.id) {
      setSelectedProviderId(integration.providerId);
      setName(integration.name);
      setIsDefault(integration.isDefault);
      setConfig(integration.config);
      setCapabilities(integration.capabilities ?? []);
      setStatus(integration.status);
      populatedForIdRef.current = integration.id;
    }
  }, [integration, isEditing]);

  // Reset form when opening for creation, e libera o ref ao fechar pra
  // permitir re-popular se reabrir a mesma integration depois.
  useEffect(() => {
    if (!isOpen) {
      populatedForIdRef.current = null;
      return;
    }
    if (!isEditing) {
      setSelectedProviderId('');
      setName('');
      setIsDefault(false);
      setConfig({});
      setCapabilities([]);
      setStatus('active');
    }
  }, [isOpen, isEditing]);

  const selectedProvider = providers?.find(p => p.id === selectedProviderId);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiPost('/integrations', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      toast.success('Integracao criada com sucesso');
      onClose();
    },
    onError: () => toast.error('Erro ao criar integracao'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiPut(`/integrations/${integrationId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-integration', integrationId] });
      toast.success('Integracao atualizada com sucesso');
      onClose();
    },
    // Sem onError de propósito: o interceptor do axios (lib/api.ts:100-103) já dispara
    // toast.error(getErrorMessage(error)), e getErrorMessage já lê response.data.message.
    // Um onError aqui só empilharia um segundo toast com o mesmo texto. Isso importa no
    // caso real: reativar uma integração cuja capability já está em outra devolve 409 com
    // "Desmarque lá antes de marcar aqui" — o interceptor mostra essa mensagem sozinho.
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/integrations/${integrationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
      toast.success('Integracao removida com sucesso');
      onClose();
    },
    onError: () => toast.error('Erro ao remover integracao'),
  });

  const testMutation = useMutation({
    mutationFn: () => apiPost(`/integrations/${integrationId}/test`, { testType: 'send_email', testData: {} }),
    onSuccess: () => toast.success('Teste realizado com sucesso'),
    onError: () => toast.error('Falha no teste da integracao'),
  });

  // Retry manual do provisionamento dos templates de sistema (2FA + notificacao)
  // do provider oficial dedicado — usado quando o best-effort do create falhou
  // (ex: Meta fora do ar no cadastro) ou para reconsultar o status atual.
  const provisionTemplatesMutation = useMutation({
    mutationFn: () => apiPost<ProvisionTemplatesResult>(`/integrations/${integrationId}/provision-templates`),
    onSuccess: (data) => {
      toast.success(
        `2FA: ${templateStatusLabel(data.twoFactor?.status)} · Notificação: ${templateStatusLabel(data.notification?.status)} · Notif. longa: ${templateStatusLabel(data.notificationLarge?.status)}`
      );
      queryClient.invalidateQueries({ queryKey: ['admin-integration', integrationId] });
    },
    // Sem onError de proposito: o interceptor do axios (lib/api.ts:100-103) ja
    // dispara toast.error com a mensagem do backend — mesmo motivo do updateMutation acima.
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Nome e obrigatorio');
      return;
    }

    // Guard de campos obrigatorios do schema. Como o backend nao valida config de
    // fato no update (validateConfig retorna objeto, nunca lanca), salvar vazio
    // apagaria silenciosamente a config de um sender que funcionava.
    const missingRequired = Object.entries(selectedProvider?.configSchema ?? {})
      .filter(([, field]) => typeof field === 'object' && field !== null && (field as ConfigField).required)
      .map(([key]) => key)
      .filter((key) => {
        const value = config[key];
        return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
      });
    if (missingRequired.length > 0) {
      toast.error(`Preencha os campos obrigatorios: ${missingRequired.join(', ')}`);
      return;
    }

    if (isEditing) {
      updateMutation.mutate({ name: name.trim(), config, isDefault, capabilities, status });
    } else {
      if (!selectedProviderId) {
        toast.error('Selecione um provider');
        return;
      }
      // capabilities NÃO vai no create: o backend as deriva do ProviderRegistry
      // (repository.ts:37). Enviar aqui seria descartado pelo Zod — UI que mente.
      // Ajuste-as depois de criar, pela edição.
      createMutation.mutate({
        providerId: selectedProviderId,
        name: name.trim(),
        config,
        isDefault,
      });
    }
  };

  const handleDelete = () => {
    if (confirm('Deseja realmente excluir esta integracao?')) {
      deleteMutation.mutate();
    }
  };

  const handleConfigChange = (field: string, value: unknown) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Group providers by first category
  const providersByCategory = (providers || []).reduce<Record<string, ProviderDefinition[]>>((acc, provider) => {
    const category = provider.categories[0] || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(provider);
    return acc;
  }, {});

  const providerOptions = Object.entries(providersByCategory).flatMap(([category, items]) => [
    { value: `__category_${category}`, label: `--- ${CATEGORY_LABELS[category] || category} ---`, disabled: true },
    ...items.map(p => ({ value: p.id, label: p.name })),
  ]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Editar integracao' : 'Nova integracao'}
      subtitle={isEditing ? integration?.providerName : 'Configure uma nova integracao'}
      icon={<FiZap className="h-5 w-5" />}
      disabled={isSaving}
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            {isEditing && (
              <div className="flex items-center gap-2">
                <Button
                  variant="error"
                  size="sm"
                  onClick={handleDelete}
                  loading={deleteMutation.isPending}
                  disabled={isSaving}
                >
                  Excluir
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={FiPlay}
                  onClick={() => testMutation.mutate()}
                  loading={testMutation.isPending}
                  disabled={isSaving}
                >
                  Testar
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} loading={isSaving}>
              {isEditing ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Status + flags when editing */}
        {isEditing && integration && (
          <div className="space-y-2">
            <FormField
              label="Status"
              description="Inativar remove esta integração de toda resolução por provider/capability — inclusive do envio de 2FA. O atendimento dos clientes não é afetado (usa company-integrations)."
            >
              <Select
                value={status}
                onChange={(value) => setStatus(value as IntegrationStatus)}
                options={(['active', 'inactive', 'error', 'pending'] as IntegrationStatus[]).map((s) => ({
                  value: s,
                  label: STATUS_LABELS[s],
                }))}
                data-testid="integration-status-select"
              />
            </FormField>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_VARIANTS[integration.status]}>
                {STATUS_LABELS[integration.status]}
              </Badge>
              {integration.isDefault && <Badge variant="info">Padrao</Badge>}
            </div>
            {integration.lastError && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
                <span className="text-xs font-medium shrink-0" style={{ color: '#ef4444' }}>Ultimo erro:</span>
                <p className="text-xs" style={{ color: '#991b1b' }}>{integration.lastError}</p>
              </div>
            )}
          </div>
        )}

        {/* Provider selection (only on create) */}
        {!isEditing && (
          <div className="space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
              Provider
            </h4>
            <FormField label="Provider" required>
              <Select
                value={selectedProviderId}
                onChange={(value) => {
                  if (!value.startsWith('__category_')) {
                    setSelectedProviderId(value);
                    setConfig({});
                    // Auto-fill name from provider
                    const provider = providers?.find(p => p.id === value);
                    if (provider && !name) setName(provider.name);
                    // Default: marca todas as capabilities do provider selecionado
                    setCapabilities(provider?.capabilities ?? []);
                  }
                }}
                options={[
                  { value: '', label: 'Selecione um provider...' },
                  ...providerOptions,
                ]}
                placeholder="Selecione um provider"
              />
            </FormField>
          </div>
        )}

        {/* Separator */}
        {!isEditing && selectedProviderId && (
          <div className="border-t" style={{ borderColor: '#e5e7eb' }} />
        )}

        {/* Common fields */}
        {(isEditing || selectedProviderId) && (
          <div className="space-y-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
              Informacoes
            </h4>
            <FormField label="Nome" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: WhatsApp Principal"
              />
            </FormField>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                  Definir como padrao
                </span>
                <p className="text-xs" style={{ color: '#9ca3af' }}>
                  Esta integracao sera usada como padrao para as capabilities deste provider
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Dynamic config form */}
        {selectedProvider && Object.keys(selectedProvider.configSchema).length > 0 && (
          <>
            <div className="border-t" style={{ borderColor: '#e5e7eb' }} />
            <div className="space-y-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
                Configuracao
              </h4>
              <DynamicConfigForm
                schema={selectedProvider.configSchema}
                values={config}
                onChange={handleConfigChange}
              />
            </div>
          </>
        )}

        {/* Capabilities multi-select */}
        {isEditing && selectedProvider && selectedProvider.capabilities.length > 0 && (
          <>
            <div className="border-t" style={{ borderColor: '#e5e7eb' }} />
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
                Capabilities
              </h4>
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                Marque quais capabilities desta integracao estao ativas. Apenas integrations marcadas
                com uma capability serao usadas em runtime para os sub-agentes correspondentes.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {selectedProvider.capabilities.map((cap) => {
                  const checked = capabilities.includes(cap);
                  return (
                    <label
                      key={cap}
                      className="flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors"
                      style={{
                        borderColor: checked ? '#a78bfa' : '#e5e7eb',
                        backgroundColor: checked ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCapabilities([...capabilities, cap]);
                          } else {
                            setCapabilities(capabilities.filter((c) => c !== cap));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm" style={{ color: '#374151' }}>
                        {cap.replace(/_/g, ' ')}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Templates do provider oficial dedicado (WhatsApp 2FA + Notificacoes) */}
        {isEditing && integration && integration.providerId === WHATSAPP_NOTIFICATIONS_PROVIDER_ID && (
          <>
            <div className="border-t" style={{ borderColor: '#e5e7eb' }} />
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8b5cf6' }}>
                Templates (WhatsApp Oficial)
              </h4>
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                Status dos templates de sistema nesta WABA. Reprovisionar adota templates já
                aprovados na Meta ou cria novos quando ausentes.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm" style={{ color: '#374151' }}>2FA:</span>
                <Badge variant={templateStatusVariant(getTemplateStatus(integration.config, 'twoFactor'))}>
                  {templateStatusLabel(getTemplateStatus(integration.config, 'twoFactor'))}
                </Badge>
                <span className="text-sm ml-3" style={{ color: '#374151' }}>Notificação:</span>
                <Badge variant={templateStatusVariant(getTemplateStatus(integration.config, 'notification'))}>
                  {templateStatusLabel(getTemplateStatus(integration.config, 'notification'))}
                </Badge>
                <span className="text-sm ml-3" style={{ color: '#374151' }}>Notif. longa:</span>
                <Badge variant={templateStatusVariant(getTemplateStatus(integration.config, 'notificationLarge'))}>
                  {templateStatusLabel(getTemplateStatus(integration.config, 'notificationLarge'))}
                </Badge>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={FiRefreshCw}
                loading={provisionTemplatesMutation.isPending}
                disabled={isSaving}
                onClick={() => provisionTemplatesMutation.mutate()}
                data-testid="reprovision-templates"
              >
                Reprovisionar templates
              </Button>
            </div>
          </>
        )}
      </form>
    </Drawer>
  );
};
